import React, { useEffect, useRef, useState } from 'react';
import { Settings as SettingsApi } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardHeader, CardContent, CardTitle } from './ui/card';
import { Database, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const BASE_RE = /(app[A-Za-z0-9]{14,})/;

function Stat({ label, value, testId }) {
  return (
    <div className="bg-stone-50 border border-stone-200 rounded px-3 py-2" data-testid={testId}>
      <div className="text-[18px] font-semibold text-stone-800 leading-tight">{value}</div>
      <div className="text-[11px] uppercase tracking-wide text-stone-500">{label}</div>
    </div>
  );
}

export function AirtableImport({ onImported }) {
  const [token, setToken] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [importPhotos, setImportPhotos] = useState(true);
  const [aiNames, setAiNames] = useState(true);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [status, setStatus] = useState(null);
  const [imported, setImported] = useState(null);
  const [undoing, setUndoing] = useState(false);
  const pollRef = useRef(null);

  const refreshCount = () => SettingsApi.airtableImportedCount().then(setImported).catch(() => {});
  useEffect(() => { refreshCount(); }, []);

  const doUndo = async () => {
    if (!window.confirm(`Remove all ${imported.guests} imported guests and ${imported.prospects} prospects (with their photos and release-form records)? Guests you added yourself are kept.`)) return;
    setUndoing(true);
    try {
      const r = await SettingsApi.airtableUndo();
      toast({ title: `Removed ${r.removed.guests} guests, ${r.removed.prospects} prospects, ${r.removed.photos} photos` });
      setStatus(null); setPreview(null); refreshCount(); onImported?.();
    } catch (e) { toast({ title: 'Undo failed', description: e?.response?.data?.detail || e.message }); }
    finally { setUndoing(false); }
  };

  const baseId = (baseUrl.match(BASE_RE) || [])[1] || '';
  const payload = { token: token.trim(), baseId, importPhotos, aiEpisodeNames: aiNames };
  const running = !!status?.running;

  useEffect(() => () => clearInterval(pollRef.current), []);

  const startPolling = () => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const s = await SettingsApi.airtableStatus();
        setStatus(s);
        if (!s.running) {
          clearInterval(pollRef.current);
          if (s.result) { toast({ title: `Imported ${s.result.guestsImported} guests and ${s.result.prospectsImported} prospects` }); refreshCount(); onImported?.(); }
          if (s.error) toast({ title: 'Import failed', description: s.error });
        }
      } catch (e) { /* keep polling */ }
    }, 1500);
  };

  const doPreview = async () => {
    if (!payload.token || !baseId) { toast({ title: 'Enter your token and base URL' }); return; }
    setPreviewing(true); setPreview(null);
    try { setPreview(await SettingsApi.airtablePreview(payload)); }
    catch (e) { toast({ title: 'Could not read Airtable', description: e?.response?.data?.detail || e.message }); }
    finally { setPreviewing(false); }
  };

  const doRun = async () => {
    try {
      await SettingsApi.airtableRun(payload);
      setStatus({ running: true, phase: 'Starting…', done: 0, total: preview.guestsToImport + preview.prospectsToImport });
      startPolling();
    } catch (e) { toast({ title: 'Could not start import', description: e?.response?.data?.detail || e.message }); }
  };

  const pct = status?.total ? Math.round((status.done / status.total) * 100) : 0;

  return (
    <Card className="mt-4" data-testid="airtable-import-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-[15px] flex items-center gap-2">
          <Database className="w-4 h-4 text-[#722F37]" /> Import from Airtable
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {imported && imported.guests + imported.prospects > 0 && (
          <div className="flex items-center justify-between gap-3 text-[12.5px] bg-amber-50 border border-amber-200 rounded px-3 py-2" data-testid="airtable-undo-bar">
            <span className="text-stone-700"><b>{imported.guests}</b> guests and <b>{imported.prospects}</b> prospects in this app came from Airtable. Not happy with the result? You can remove exactly those records.</span>
            <Button data-testid="airtable-undo-btn" variant="outline" size="sm" onClick={doUndo} disabled={undoing || running} className="border-amber-400 text-amber-900 hover:bg-amber-100 shrink-0">
              {undoing ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Removing…</> : 'Undo Airtable import'}
            </Button>
          </div>
        )}
        <div className="text-[12.5px] text-stone-700">
          Migrates your Airtable base: <b>Podcast Appointments</b>, <b>Guest Info</b> (release forms), <b>Asset Distribution</b> (platform links &amp; photos), <b>Episodes</b> and <b>Potential Guests</b>.
          Each person becomes one guest (status <i>Recorded</i>); guests whose email already exists here are skipped. Test rows and calendar blocks are filtered out. Tip: download a backup (above) first — and you can undo the import afterwards.
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-[11px] uppercase text-stone-500">Personal access token</Label>
            <Input data-testid="airtable-token-input" type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="pat…" className="font-mono" disabled={running} />
          </div>
          <div>
            <Label className="text-[11px] uppercase text-stone-500">Base URL or ID</Label>
            <Input data-testid="airtable-base-input" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://airtable.com/appXXXX/…" className="font-mono" disabled={running} />
            {baseUrl && !baseId && <div className="text-[11px] text-red-600 mt-1">Couldn’t find an “app…” id in that URL</div>}
          </div>
        </div>
        <div className="flex flex-wrap gap-4 text-[12.5px] text-stone-700">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" data-testid="airtable-photos-toggle" checked={importPhotos} onChange={(e) => setImportPhotos(e.target.checked)} disabled={running} />
            Import photos as thumbnails (shrunk to 1400px)
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" data-testid="airtable-ai-toggle" checked={aiNames} onChange={(e) => setAiNames(e.target.checked)} disabled={running} />
            Use AI to name guests on title-only episodes
          </label>
        </div>
        <div className="flex justify-end gap-2">
          <Button data-testid="airtable-preview-btn" variant="outline" onClick={doPreview} disabled={previewing || running || !token || !baseId}>
            {previewing ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Reading Airtable…</> : 'Preview import'}
          </Button>
          <Button data-testid="airtable-run-btn" onClick={doRun} disabled={!preview || running || preview.guestsToImport + preview.prospectsToImport === 0} className="bg-[#722F37] hover:bg-[#5a252c] text-white">
            {running ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Importing…</> : `Import ${preview ? preview.guestsToImport + preview.prospectsToImport : ''} records`}
          </Button>
        </div>

        {preview && !status && (
          <div className="space-y-3" data-testid="airtable-preview">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Stat label="Guests to import" value={preview.guestsToImport} testId="airtable-stat-guests" />
              <Stat label="Prospects to import" value={preview.prospectsToImport} testId="airtable-stat-prospects" />
              <Stat label="Already in app" value={preview.guestsAlreadyExist} testId="airtable-stat-existing" />
              <Stat label="Junk rows skipped" value={preview.junkSkipped} testId="airtable-stat-junk" />
              <Stat label="With email" value={preview.withEmail} />
              <Stat label="With recording date" value={preview.withRecordingDate} />
              <Stat label="Signed release forms" value={preview.withReleaseForm} />
              <Stat label="With photo" value={preview.withPhoto} />
              <Stat label="With platform links" value={preview.withPlatformLinks} />
              <Stat label="With episode title" value={preview.withEpisode} />
              <Stat label="Named by AI" value={preview.aiNamedEpisodes} />
              <Stat label="Episodes w/o guest" value={preview.episodesWithoutGuest} />
            </div>
            <div className="text-[11.5px] text-stone-500">
              Airtable tables read: {Object.entries(preview.tables).map(([k, v]) => `${k} ${v}`).join(' · ')}
            </div>
            <div>
              <div className="text-[11px] uppercase text-stone-500 mb-1">Sample of what will be created</div>
              <div className="border border-stone-200 rounded divide-y divide-stone-100 text-[12px]">
                {preview.sampleGuests.map((g, i) => (
                  <div key={i} className="px-3 py-1.5 flex flex-wrap gap-x-3 gap-y-0.5">
                    <span className="font-medium text-stone-800">{g.name}</span>
                    <span className="text-stone-500">{g.email || '— no email'}</span>
                    {g.recordingDate && <span className="text-stone-500">{g.recordingDate.slice(0, 10)}</span>}
                    {g.release === 'signed' && <span className="text-emerald-700">release signed</span>}
                    {g.photo && <span className="text-stone-500">photo</span>}
                    {g.episodeTitle && <span className="text-stone-500 truncate max-w-[280px]">“{g.episodeTitle}”</span>}
                  </div>
                ))}
              </div>
            </div>
            {preview.sampleJunk?.length > 0 && (
              <div className="text-[11.5px] text-stone-500">Skipped as junk (sample): {preview.sampleJunk.join(' · ')}</div>
            )}
            {preview.sampleEpisodesWithoutGuest?.length > 0 && (
              <div className="text-[11.5px] text-stone-500">Episodes with no identifiable guest (not imported, sample): {preview.sampleEpisodesWithoutGuest.slice(0, 5).join(' · ')}</div>
            )}
          </div>
        )}

        {status && (
          <div className="space-y-2" data-testid="airtable-progress">
            <div className="flex items-center gap-2 text-[12.5px] text-stone-700">
              {running ? <Loader2 className="w-4 h-4 animate-spin text-[#722F37]" /> : status.error ? <AlertCircle className="w-4 h-4 text-red-600" /> : <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
              <span data-testid="airtable-phase">{status.error ? `Failed: ${status.error}` : status.phase}</span>
              {status.total > 0 && <span className="text-stone-500">{status.done}/{status.total}</span>}
            </div>
            {running && (
              <div className="h-2 bg-stone-200 rounded overflow-hidden">
                <div className="h-full bg-[#722F37] transition-[width] duration-500" style={{ width: `${pct}%` }} />
              </div>
            )}
            {status.result && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="airtable-result">
                <Stat label="Guests imported" value={status.result.guestsImported} testId="airtable-result-guests" />
                <Stat label="Prospects imported" value={status.result.prospectsImported} />
                <Stat label="Release forms" value={status.result.releaseFormsImported} />
                <Stat label="Photos" value={status.result.photosImported} />
              </div>
            )}
            {!running && (
              <div className="flex justify-end">
                <Button variant="ghost" size="sm" onClick={() => { setStatus(null); setPreview(null); }}>Start another import</Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
